<template>
  <div class="login-page" :class="{ 'is-mounted': mounted }">

    <!-- LEFT PANEL — Brand & Quote -->
    <div class="login-left">
      <div class="left-grid-bg" />
      <div class="left-glow" />

      <div class="left-inner">
        <!-- Brand -->
        <div class="brand-row">
          <div class="brand-mark">EMS</div>
          <div class="brand-text">
            <div class="brand-name">Enrico Michael Severe</div>
            <div class="brand-role">Private Researcher · Author · Founder</div>
          </div>
        </div>

        <!-- Decorative book stack -->
        <div class="book-stack">
          <div class="stacked-book book-1">
            <div class="sb-crown">♛</div>
            <div class="sb-title">THE<br><strong>BENEVOLENT<br>ECONOMY</strong></div>
            <div class="sb-divider"></div>
            <div class="sb-scales">⚖</div>
            <div class="sb-author">ENRICO MICHAEL SEVERE</div>
            <div class="sb-heir">Davidic Heir, Son of King David</div>
          </div>
          <div class="stacked-book book-2">
            <div class="sb-crown">♛</div>
            <div class="sb-title">THE<br><strong>BENEVOLENT<br>CURE</strong></div>
            <div class="sb-divider"></div>
            <div class="sb-scales">⚖</div>
            <div class="sb-author">ENRICO MICHAEL SEVERE</div>
            <div class="sb-heir">Davidic Heir, Son of King David</div>
          </div>
        </div>

        <!-- Quote -->
        <div class="left-quote">
          <div class="quote-ornament">"</div>
          <p class="quote-body">Walk with the wise and become wise, for a companion of fools suffers harm.</p>
          <div class="quote-source">— Solomon, Proverbs 13:20</div>
        </div>

        <!-- Stats row -->
        <div class="left-stats">
          <div class="ls-item">
            <div class="ls-num">6+</div>
            <div class="ls-label">Books Published</div>
          </div>
          <div class="ls-divider" />
          <div class="ls-item">
            <div class="ls-num">2</div>
            <div class="ls-label">Series Volumes</div>
          </div>
          <div class="ls-divider" />
          <div class="ls-item">
            <div class="ls-num">∞</div>
            <div class="ls-label">Ideas in Progress</div>
          </div>
        </div>
      </div>
    </div>

    <!-- RIGHT PANEL — Login Form -->
    <div class="login-right">
      <div class="right-inner">
        <div class="form-header">
          <div class="form-eyebrow">Restricted Access</div>
          <h1 class="form-title">Dashboard Login</h1>
          <p class="form-sub">Sign in to manage content, posts, and settings</p>
        </div>

        <form @submit.prevent="handleLogin" class="login-form">
          <div class="input-group">
            <label for="email">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                <polyline points="22,6 12,13 2,6"></polyline>
              </svg>
              Email Address
            </label>
            <input
              id="email"
              type="email"
              v-model="email"
              placeholder="your@email.com"
              autocomplete="email"
              required
            />
          </div>

          <div class="input-group">
            <label for="password">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
              </svg>
              Password
            </label>
            <div class="password-wrapper">
              <input
                id="password"
                :type="showPassword ? 'text' : 'password'"
                v-model="password"
                placeholder="••••••••"
                autocomplete="current-password"
                required
              />
              <button type="button" class="toggle-pw" @click="showPassword = !showPassword" tabindex="-1">
                <svg v-if="!showPassword" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                  <circle cx="12" cy="12" r="3"></circle>
                </svg>
                <svg v-else width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                  <line x1="1" y1="1" x2="23" y2="23"></line>
                </svg>
              </button>
            </div>
          </div>

          <transition name="err">
            <div v-if="error" class="error-message">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
              </svg>
              {{ error }}
            </div>
          </transition>

          <button type="submit" class="login-button" :disabled="loading">
            <span v-if="loading" class="btn-loading">
              <span class="btn-spinner"></span>
              Authenticating...
            </span>
            <span v-else class="btn-label">
              Access Dashboard
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                <line x1="5" y1="12" x2="19" y2="12"></line>
                <polyline points="12 5 19 12 12 19"></polyline>
              </svg>
            </span>
          </button>
        </form>

        <div class="form-footer">
          <div class="form-footer-divider" />
          <p class="form-footer-text">
            Admin access only. Return to
            <router-link to="/" class="form-footer-link">the site →</router-link>
          </p>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import { signInWithEmailAndPassword } from 'firebase/auth'
import { auth } from '../firebase'

export default {
  name: 'LoginView',
  data () {
    return {
      email: '',
      password: '',
      error: '',
      loading: false,
      mounted: false,
      showPassword: false,
    }
  },
  mounted () {
    setTimeout(() => { this.mounted = true }, 80)
  },
  methods: {
    async handleLogin () {
      this.error = ''
      this.loading = true
      try {
        await signInWithEmailAndPassword(auth, this.email, this.password)
        this.$router.push('/admin')
      } catch (err) {
        const messages = {
          'auth/invalid-credential': 'Incorrect email or password.',
          'auth/user-not-found': 'No account found with that email.',
          'auth/wrong-password': 'Incorrect password.',
          'auth/too-many-requests': 'Too many attempts. Try again later.',
          'auth/invalid-email': 'Please enter a valid email address.',
        }
        this.error = messages[err.code] || 'Authentication failed. Please try again.'
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,700;0,900;1,400&family=Inter:wght@400;500;600;700&display=swap');

/* ─── Layout ───────────────────────────────────────────────── */
.login-page {
  min-height: 100vh;
  display: grid;
  grid-template-columns: 1fr 1fr;
  opacity: 0;
  transition: opacity 0.6s ease;
}

.login-page.is-mounted { opacity: 1; }

@media (max-width: 900px) {
  .login-page { grid-template-columns: 1fr; }
  .login-left { display: none; }
}

/* ─── LEFT PANEL ───────────────────────────────────────────── */
.login-left {
  background: #0b1628;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 60px 48px;
}

.left-grid-bg {
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(rgba(201,168,76,0.05) 1px, transparent 1px),
    linear-gradient(90deg, rgba(201,168,76,0.05) 1px, transparent 1px);
  background-size: 56px 56px;
  pointer-events: none;
}

.left-glow {
  position: absolute;
  top: -180px;
  right: -180px;
  width: 600px;
  height: 600px;
  background: radial-gradient(circle, rgba(201,168,76,0.09) 0%, transparent 70%);
  pointer-events: none;
}

.left-inner {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 440px;
  display: flex;
  flex-direction: column;
  gap: 40px;
  animation: panelIn 0.9s cubic-bezier(0.16,1,0.3,1) 0.1s both;
}

@keyframes panelIn {
  from { opacity: 0; transform: translateX(-24px); }
  to   { opacity: 1; transform: translateX(0); }
}

/* Brand */
.brand-row {
  display: flex;
  align-items: center;
  gap: 16px;
}

.brand-mark {
  width: 52px;
  height: 52px;
  background: #c9a84c;
  color: #0b1628;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'Playfair Display', serif;
  font-weight: 900;
  font-size: 18px;
  letter-spacing: 1px;
  flex-shrink: 0;
}

.brand-name {
  font-family: 'Playfair Display', serif;
  font-size: 18px;
  font-weight: 700;
  color: #f5f0e8;
  margin-bottom: 4px;
}

.brand-role {
  font-family: 'Inter', sans-serif;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #8a6e2a;
}

/* Book stack */
.book-stack {
  position: relative;
  height: 220px;
}

.stacked-book {
  position: absolute;
  width: 140px;
  min-height: 200px;
  padding: 18px 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 6px;
  border: 1px solid rgba(201,168,76,0.4);
  box-shadow: 6px 6px 24px rgba(0,0,0,0.5);
}

.stacked-book::before {
  content: '';
  position: absolute;
  inset: 5px;
  border: 1px solid rgba(201,168,76,0.18);
  pointer-events: none;
}

.book-1 {
  background: linear-gradient(160deg, #0d1f3c 0%, #0b1628 60%, #071020 100%);
  left: 0;
  top: 10px;
  transform: rotate(-4deg);
  transition: transform 0.4s ease;
  z-index: 1;
}

.book-2 {
  background: linear-gradient(160deg, #123524 0%, #0e2a1c 60%, #061309 100%);
  left: 80px;
  top: 0;
  transform: rotate(3deg);
  transition: transform 0.4s ease;
  z-index: 2;
}

.book-stack:hover .book-1 { transform: rotate(-8deg) translateX(-8px); }
.book-stack:hover .book-2 { transform: rotate(6deg) translateX(8px); }

.sb-crown { font-size: 16px; filter: sepia(1) hue-rotate(15deg) saturate(3) brightness(1.2); }
.sb-title {
  font-family: 'Playfair Display', serif;
  font-size: 7px;
  color: #e8cc80;
  letter-spacing: 0.06em;
  line-height: 1.35;
}
.sb-title strong { font-size: 9px; font-weight: 900; display: block; margin-top: 2px; }
.sb-divider { width: 70%; height: 1px; background: linear-gradient(90deg, transparent, #c9a84c, transparent); }
.sb-scales { font-size: 14px; filter: sepia(1) hue-rotate(15deg) saturate(3) brightness(1.1); }
.sb-author { font-family: 'Playfair Display', serif; font-size: 6px; font-weight: 700; color: #e8cc80; letter-spacing: 0.1em; }
.sb-heir { font-family: 'Inter', sans-serif; font-size: 5px; font-style: italic; color: #c8c0b0; }

/* Quote */
.left-quote {
  border-left: 3px solid rgba(201,168,76,0.4);
  padding: 20px 24px;
  background: rgba(201,168,76,0.04);
}

.quote-ornament {
  font-family: 'Playfair Display', serif;
  font-size: 52px;
  color: #c9a84c;
  line-height: 0.8;
  margin-bottom: 12px;
  opacity: 0.7;
}

.quote-body {
  font-family: 'Playfair Display', serif;
  font-style: italic;
  font-size: 16px;
  line-height: 1.65;
  color: #c8c0b0;
  margin-bottom: 12px;
}

.quote-source {
  font-family: 'Inter', sans-serif;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #8a6e2a;
}

/* Stats */
.left-stats {
  display: flex;
  align-items: center;
  gap: 0;
  border: 1px solid rgba(201,168,76,0.12);
}

.ls-item { padding: 16px 24px; text-align: center; flex: 1; }
.ls-num { font-family: 'Playfair Display', serif; font-size: 26px; font-weight: 700; color: #c9a84c; line-height: 1; margin-bottom: 4px; }
.ls-label { font-family: 'Inter', sans-serif; font-size: 9px; font-weight: 600; letter-spacing: 0.12em; text-transform: uppercase; color: #4b5563; }
.ls-divider { width: 1px; height: 52px; background: rgba(201,168,76,0.12); flex-shrink: 0; }

/* ─── RIGHT PANEL ──────────────────────────────────────────── */
.login-right {
  background: #0d1117;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 60px 48px;
  border-left: 1px solid rgba(201,168,76,0.08);
}

.right-inner {
  width: 100%;
  max-width: 400px;
  animation: panelIn2 0.9s cubic-bezier(0.16,1,0.3,1) 0.2s both;
}

@keyframes panelIn2 {
  from { opacity: 0; transform: translateY(24px); }
  to   { opacity: 1; transform: translateY(0); }
}

/* Form header */
.form-eyebrow {
  font-family: 'Inter', sans-serif;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.28em;
  text-transform: uppercase;
  color: #c9a84c;
  margin-bottom: 14px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.form-eyebrow::before {
  content: '';
  display: block;
  width: 24px;
  height: 1px;
  background: #c9a84c;
}

.form-title {
  font-family: 'Playfair Display', serif;
  font-size: 38px;
  font-weight: 700;
  color: #f5f0e8;
  margin-bottom: 10px;
  letter-spacing: -0.5px;
  line-height: 1.15;
}

.form-sub {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  color: #4b5563;
  font-weight: 400;
  margin-bottom: 40px;
}

/* Form fields */
.login-form { display: flex; flex-direction: column; gap: 22px; }

.input-group { display: flex; flex-direction: column; gap: 8px; }

.input-group label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: 'Inter', sans-serif;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #718096;
}

.input-group label svg { color: #4b5563; }

.input-group input,
.password-wrapper input {
  width: 100%;
  padding: 15px 16px;
  background: #0f1a2e;
  border: 1px solid rgba(201,168,76,0.15);
  color: #f5f0e8;
  font-family: 'Inter', sans-serif;
  font-size: 15px;
  transition: border-color 0.2s, box-shadow 0.2s;
  outline: none;
}

.input-group input::placeholder,
.password-wrapper input::placeholder {
  color: #2d3748;
}

.input-group input:focus,
.password-wrapper input:focus {
  border-color: rgba(201,168,76,0.5);
  box-shadow: 0 0 0 3px rgba(201,168,76,0.08);
}

.password-wrapper {
  position: relative;
}

.password-wrapper input { padding-right: 48px; }

.toggle-pw {
  position: absolute;
  right: 14px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #4b5563;
  cursor: pointer;
  padding: 4px;
  transition: color 0.2s;
}

.toggle-pw:hover { color: #c9a84c; }

/* Error */
.error-message {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 13px 16px;
  background: rgba(239,68,68,0.08);
  border: 1px solid rgba(239,68,68,0.25);
  color: #fc8181;
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  font-weight: 500;
}

.err-enter-active, .err-leave-active { transition: opacity 0.25s ease, transform 0.25s ease; }
.err-enter-from { opacity: 0; transform: translateY(-6px); }
.err-leave-to   { opacity: 0; transform: translateY(-6px); }

/* Button */
.login-button {
  width: 100%;
  padding: 16px 24px;
  background: #c9a84c;
  color: #0b1628;
  border: none;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  cursor: pointer;
  transition: background 0.2s, transform 0.2s;
  margin-top: 6px;
}

.login-button:hover:not(:disabled) {
  background: #e8cc80;
  transform: translateY(-2px);
}

.login-button:active:not(:disabled) { transform: translateY(0); }

.login-button:disabled { opacity: 0.6; cursor: not-allowed; }

.btn-label {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
}

.btn-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
}

.btn-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(11,22,40,0.3);
  border-top-color: #0b1628;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
  flex-shrink: 0;
}

@keyframes spin { to { transform: rotate(360deg); } }

/* Footer */
.form-footer {
  margin-top: 36px;
}

.form-footer-divider {
  height: 1px;
  background: rgba(201,168,76,0.08);
  margin-bottom: 20px;
}

.form-footer-text {
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  color: #4b5563;
  text-align: center;
}

.form-footer-link {
  color: #c9a84c;
  text-decoration: none;
  font-weight: 600;
  transition: color 0.2s;
}

.form-footer-link:hover { color: #e8cc80; }
</style>